"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import WaveDropsScrollingOverlay from "@/components/waves/drops/WaveDropsScrollingOverlay";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { Drop, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { isWaveDirectMessage } from "@/helpers/waves/wave.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useScrollBehavior } from "@/hooks/useScrollBehavior";
import { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import { useWaveBoostedDrops } from "@/hooks/useWaveBoostedDrops";
import { useWaveIsTyping } from "@/hooks/useWaveIsTyping";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TweetPreviewModeProvider, type TweetPreviewMode } from "@/components/tweets/TweetPreviewModeContext";
import { useWaveDropsClipboard } from "./hooks/useWaveDropsClipboard";
import { useWaveDropsNotificationRead } from "./hooks/useWaveDropsNotificationRead";
import { useWaveDropsSerialScroll } from "./hooks/useWaveDropsSerialScroll";
import { WaveDropsContent } from "./subcomponents/WaveDropsContent";
const EMPTY_DROPS: Drop[] = [];
interface WaveDropsAllProps {
  readonly waveId: string;
  readonly dropId: string | null;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly initialDrop: number | null;
  readonly dividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly bottomPaddingClassName?: string | undefined;
  readonly isMuted?: boolean | undefined;
}
const WaveDropsAllInner: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(104);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const router = useRouter();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    connectedProfile
  } = useAuth();
  const {
    isAppleMobile
  } = useDeviceInfo();
  const containerRef = useRef(null);
  const {
    waveMessages,
    fetchNextPage,
    waitAndRevealDrop
  } = useVirtualizedWaveDrops(waveId, dropId);
  const latestSerialNo = waveMessages?.drops[0]?.serial_no ?? null;
  const latestSerialNoRef = useRef(null);
  const [pinnedLatestSerial, setPinnedLatestSerial] = useState(null);
  let t2;
  let t3;
  if ($[0] !== latestSerialNo) {
    t2 = () => {
      latestSerialNoRef.current = latestSerialNo;
    };
    t3 = [latestSerialNo];
    $[0] = latestSerialNo;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const typingMessage = useWaveIsTyping(waveId, connectedProfile?.handle ?? null, isMuted);
  let t4;
  if ($[3] !== waveId) {
    t4 = {
      waveId
    };
    $[3] = waveId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: boostedDrops
  } = useWaveBoostedDrops(t4);
  let t5;
  if ($[5] !== isAppleMobile) {
    t5 = nextShouldPinToBottom => {
      if (!isAppleMobile) {
        return;
      }
      if (nextShouldPinToBottom) {
        setPinnedLatestSerial(null);
        return;
      }
      const latestSerial = latestSerialNoRef.current;
      if (latestSerial === null) {
        return;
      }
      setPinnedLatestSerial(previous => previous ?? latestSerial);
    };
    $[5] = isAppleMobile;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handlePinStateChange = t5;
  let t6;
  if ($[7] !== handlePinStateChange) {
    t6 = {
      onPinStateChange: handlePinStateChange
    };
    $[7] = handlePinStateChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const scrollBehavior = useScrollBehavior(t6);
  const {
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    isAtBottom,
    shouldPinToBottom,
    scrollToVisualBottom,
    forcePinToBottom
  } = scrollBehavior;
  let t7;
  if ($[9] !== removeWaveDeliveredNotifications || $[10] !== waveId) {
    t7 = {
      waveId,
      removeWaveDeliveredNotifications
    };
    $[9] = removeWaveDeliveredNotifications;
    $[10] = waveId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useWaveDropsNotificationRead(t7);
  waveMessages?.drops;
  const dropsForClipboard = waveMessages?.drops ?? EMPTY_DROPS;
  let t8;
  if ($[12] !== dropsForClipboard) {
    t8 = {
      containerRef,
      drops: dropsForClipboard
    };
    $[12] = dropsForClipboard;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useWaveDropsClipboard(t8);
  const initializedWaveRef = useRef(null);
  let t10;
  let t9;
  if ($[14] !== dividerSerialNo || $[15] !== setUnreadDividerSerialNo || $[16] !== waveId) {
    t9 = () => {
      if (initializedWaveRef.current === waveId) {
        return;
      }
      initializedWaveRef.current = waveId;
      setUnreadDividerSerialNo(dividerSerialNo ?? null);
    };
    t10 = [waveId, dividerSerialNo, setUnreadDividerSerialNo];
    $[14] = dividerSerialNo;
    $[15] = setUnreadDividerSerialNo;
    $[16] = waveId;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  let visibleLatestSerial;
  if ($[19] !== isAppleMobile || $[20] !== latestSerialNo || $[21] !== pinnedLatestSerial || $[22] !== shouldPinToBottom) {
    visibleLatestSerial = null;
    if (latestSerialNo !== null) {
      if (!isAppleMobile || shouldPinToBottom) {
        visibleLatestSerial = latestSerialNo;
      } else {
        visibleLatestSerial = pinnedLatestSerial ?? latestSerialNo;
      }
    }
    $[19] = isAppleMobile;
    $[20] = latestSerialNo;
    $[21] = pinnedLatestSerial;
    $[22] = shouldPinToBottom;
    $[23] = visibleLatestSerial;
  } else {
    visibleLatestSerial = $[23];
  }
  visibleLatestSerial;
  let t11;
  bb0: {
    if (!waveMessages) {
      t11 = waveMessages;
      break bb0;
    }
    if (!isAppleMobile || visibleLatestSerial === null) {
      t11 = waveMessages;
      break bb0;
    }
    let t12;
    if ($[24] !== visibleLatestSerial || $[25] !== waveMessages.drops) {
      let t13;
      if ($[27] !== visibleLatestSerial) {
        t13 = drop => {
          if (typeof drop.serial_no !== "number") {
            return true;
          }
          return drop.serial_no <= visibleLatestSerial;
        };
        $[27] = visibleLatestSerial;
        $[28] = t13;
      } else {
        t13 = $[28];
      }
      t12 = waveMessages.drops.filter(t13);
      $[24] = visibleLatestSerial;
      $[25] = waveMessages.drops;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    const filteredDrops = t12;
    if (filteredDrops.length === waveMessages.drops.length) {
      t11 = waveMessages;
      break bb0;
    }
    let t13;
    if ($[29] !== filteredDrops || $[30] !== waveMessages) {
      t13 = {
        ...waveMessages,
        drops: filteredDrops
      };
      $[29] = filteredDrops;
      $[30] = waveMessages;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    t11 = t13;
  }
  const renderedWaveMessages = t11;
  visibleLatestSerial;
  let t12;
  bb1: {
    if (!isAppleMobile || !waveMessages?.drops?.length || visibleLatestSerial === null) {
      t12 = 0;
      break bb1;
    }
    let t13;
    if ($[32] !== visibleLatestSerial || $[33] !== waveMessages.drops) {
      let t14;
      if ($[35] !== visibleLatestSerial) {
        t14 = (count, drop_0) => {
          if (typeof drop_0.serial_no !== "number") {
            return count;
          }
          return drop_0.serial_no > visibleLatestSerial ? count + 1 : count;
        };
        $[35] = visibleLatestSerial;
        $[36] = t14;
      } else {
        t14 = $[36];
      }
      t13 = waveMessages.drops.reduce(t14, 0);
      $[32] = visibleLatestSerial;
      $[33] = waveMessages.drops;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    t12 = t13;
  }
  const pendingDropsCount = t12;
  let t13;
  if ($[37] !== dropId || $[38] !== fetchNextPage || $[39] !== initialDrop || $[40] !== renderedWaveMessages || $[41] !== scrollContainerRef || $[42] !== scrollToVisualBottom || $[43] !== shouldPinToBottom || $[44] !== waitAndRevealDrop || $[45] !== waveId || $[46] !== waveMessages) {
    t13 = {
      waveId,
      dropId,
      initialDrop,
      waveMessages,
      renderedWaveMessages,
      fetchNextPage,
      waitAndRevealDrop,
      scrollContainerRef,
      shouldPinToBottom,
      scrollToVisualBottom
    };
    $[37] = dropId;
    $[38] = fetchNextPage;
    $[39] = initialDrop;
    $[40] = renderedWaveMessages;
    $[41] = scrollContainerRef;
    $[42] = scrollToVisualBottom;
    $[43] = shouldPinToBottom;
    $[44] = waitAndRevealDrop;
    $[45] = waveId;
    $[46] = waveMessages;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const {
    serialTarget,
    queueSerialTarget,
    targetDropRef,
    isScrolling,
    scrollBaselineSerials,
    frozenAutoCollapseSerials
  } = useWaveDropsSerialScroll(t13);
  let t14;
  if ($[48] !== frozenAutoCollapseSerials || $[49] !== isScrolling || $[50] !== renderedWaveMessages?.drops || $[51] !== scrollBaselineSerials) {
    t14 = () => {
      if (!isScrolling || !scrollBaselineSerials) {
        return frozenAutoCollapseSerials;
      }
      const drops = renderedWaveMessages?.drops;
      if (!drops || drops.length === 0) {
        return frozenAutoCollapseSerials;
      }
      const next = new Set(frozenAutoCollapseSerials);
      for (const drop_1 of drops) {
        const serialNo = drop_1.serial_no;
        if (typeof serialNo !== "number") {
          continue;
        }
        if (!scrollBaselineSerials.has(serialNo) && !next.has(serialNo)) {
          next.add(serialNo);
        }
      }
      return next;
    };
    $[48] = frozenAutoCollapseSerials;
    $[49] = isScrolling;
    $[50] = renderedWaveMessages?.drops;
    $[51] = scrollBaselineSerials;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  renderedWaveMessages?.drops;
  let t15;
  if ($[53] !== t14) {
    t15 = t14();
    $[53] = t14;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const autoCollapseSerials = t15;
  const waveChatScroll = useWaveChatScrollOptional();
  let t16;
  let t17;
  if ($[55] !== queueSerialTarget || $[56] !== waveChatScroll || $[57] !== waveId) {
    t16 = () => {
      if (!waveChatScroll) {
        return;
      }
      return waveChatScroll.registerScrollHandler({
        waveId,
        handler: queueSerialTarget
      });
    };
    t17 = [waveChatScroll, waveId, queueSerialTarget];
    $[55] = queueSerialTarget;
    $[56] = waveChatScroll;
    $[57] = waveId;
    $[58] = t16;
    $[59] = t17;
  } else {
    t16 = $[58];
    t17 = $[59];
  }
  useEffect(t16, t17);
  let t18;
  if ($[60] !== forcePinToBottom || $[61] !== waveMessages?.drops?.length) {
    t18 = () => {
      if (!waveMessages?.drops?.length) {
        return;
      }
      forcePinToBottom();
    };
    $[60] = forcePinToBottom;
    $[61] = waveMessages?.drops?.length;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  const revealPendingDrops = t18;
  let t19;
  if ($[63] !== dropId || $[64] !== fetchNextPage || $[65] !== waveId || $[66] !== waveMessages?.hasNextPage || $[67] !== waveMessages?.isLoading || $[68] !== waveMessages?.isLoadingNextPage) {
    t19 = async () => {
      if (waveMessages?.hasNextPage && !waveMessages?.isLoading && !waveMessages?.isLoadingNextPage) {
        await fetchNextPage({
          waveId,
          type: DropSize.FULL
        }, dropId);
      }
    };
    $[63] = dropId;
    $[64] = fetchNextPage;
    $[65] = waveId;
    $[66] = waveMessages?.hasNextPage;
    $[67] = waveMessages?.isLoading;
    $[68] = waveMessages?.isLoadingNextPage;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  waveMessages?.hasNextPage;
  waveMessages?.isLoading;
  waveMessages?.isLoadingNextPage;
  const handleTopIntersection = t19;
  let t20;
  if ($[70] !== queueSerialTarget || $[71] !== router || $[72] !== waveId) {
    t20 = drop_2 => {
      if (drop_2.wave.id === waveId) {
        queueSerialTarget(drop_2.serial_no);
      } else {
        const waveDetails = drop_2.wave as unknown as {
          chat?: {
            scope?: {
              group?: {
                is_direct_message?: boolean | undefined;
              } | undefined;
            } | undefined;
          } | undefined;
        } ?? undefined;
        const isDirectMessage = isWaveDirectMessage(drop_2.wave.id, waveDetails);
        const href = getWaveRoute({
          waveId: drop_2.wave.id,
          serialNo: drop_2.serial_no,
          isDirectMessage,
          isApp: false
        });
        router.push(href);
      }
    };
    $[70] = queueSerialTarget;
    $[71] = router;
    $[72] = waveId;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  const handleQuoteClick = t20;
  let t21;
  if ($[74] !== activeDrop || $[75] !== autoCollapseSerials || $[76] !== boostedDrops || $[77] !== bottomAnchorCallbackRef || $[78] !== bottomAnchorRef || $[79] !== bottomPaddingClassName || $[80] !== dropId || $[81] !== forcePinToBottom || $[82] !== handleQuoteClick || $[83] !== handleTopIntersection || $[84] !== isAtBottom || $[85] !== onDropContentClick || $[86] !== onReply || $[87] !== pendingDropsCount || $[88] !== queueSerialTarget || $[89] !== renderedWaveMessages || $[90] !== revealPendingDrops || $[91] !== scrollBehavior.handleScroll || $[92] !== scrollContainerCallbackRef || $[93] !== scrollContainerRef || $[94] !== serialTarget || $[95] !== targetDropRef || $[96] !== typingMessage || $[97] !== unreadCount) {
    t21 = <TweetPreviewModeProvider mode="never"><WaveDropsContent waveMessages={renderedWaveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} scrollContainerCallbackRef={scrollContainerCallbackRef} bottomAnchorRef={bottomAnchorRef} bottomAnchorCallbackRef={bottomAnchorCallbackRef} onTopIntersection={handleTopIntersection} onReply={onReply} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={handleQuoteClick} isAtBottom={isAtBottom} scrollToBottom={forcePinToBottom} onScroll={scrollBehavior.handleScroll} typingMessage={typingMessage} onDropContentClick={onDropContentClick} pendingCount={pendingDropsCount} onRevealPending={revealPendingDrops} bottomPaddingClassName={bottomPaddingClassName} boostedDrops={boostedDrops} onBoostedDropClick={queueSerialTarget} onScrollToUnread={queueSerialTarget} unreadCount={unreadCount} autoCollapseSerials={autoCollapseSerials} /></TweetPreviewModeProvider>;
    $[74] = activeDrop;
    $[75] = autoCollapseSerials;
    $[76] = boostedDrops;
    $[77] = bottomAnchorCallbackRef;
    $[78] = bottomAnchorRef;
    $[79] = bottomPaddingClassName;
    $[80] = dropId;
    $[81] = forcePinToBottom;
    $[82] = handleQuoteClick;
    $[83] = handleTopIntersection;
    $[84] = isAtBottom;
    $[85] = onDropContentClick;
    $[86] = onReply;
    $[87] = pendingDropsCount;
    $[88] = queueSerialTarget;
    $[89] = renderedWaveMessages;
    $[90] = revealPendingDrops;
    $[91] = scrollBehavior.handleScroll;
    $[92] = scrollContainerCallbackRef;
    $[93] = scrollContainerRef;
    $[94] = serialTarget;
    $[95] = targetDropRef;
    $[96] = typingMessage;
    $[97] = unreadCount;
    $[98] = t21;
  } else {
    t21 = $[98];
  }
  let t22;
  if ($[99] !== isScrolling) {
    t22 = <WaveDropsScrollingOverlay isVisible={isScrolling} />;
    $[99] = isScrolling;
    $[100] = t22;
  } else {
    t22 = $[100];
  }
  let t23;
  if ($[101] !== t21 || $[102] !== t22) {
    t23 = <div ref={containerRef} className="tw-relative tw-flex tw-h-full tw-flex-col tw-justify-end tw-overflow-hidden tw-bg-iron-950">{t21}{t22}</div>;
    $[101] = t21;
    $[102] = t22;
    $[103] = t23;
  } else {
    t23 = $[103];
  }
  return t23;
};
export const WaveDropsAllWithoutProvider: React.FC<WaveDropsAllProps> = WaveDropsAllInner;
const WaveDropsAll: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(15);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const t2 = dividerSerialNo ?? null;
  const t3 = `unread-divider-${waveId}`;
  let t4;
  if ($[0] !== activeDrop || $[1] !== bottomPaddingClassName || $[2] !== dividerSerialNo || $[3] !== dropId || $[4] !== initialDrop || $[5] !== isMuted || $[6] !== onDropContentClick || $[7] !== onReply || $[8] !== unreadCount || $[9] !== waveId) {
    t4 = <WaveDropsAllInner key={waveId} waveId={waveId} dropId={dropId} onReply={onReply} activeDrop={activeDrop} initialDrop={initialDrop} dividerSerialNo={dividerSerialNo} unreadCount={unreadCount} onDropContentClick={onDropContentClick} bottomPaddingClassName={bottomPaddingClassName} isMuted={isMuted} />;
    $[0] = activeDrop;
    $[1] = bottomPaddingClassName;
    $[2] = dividerSerialNo;
    $[3] = dropId;
    $[4] = initialDrop;
    $[5] = isMuted;
    $[6] = onDropContentClick;
    $[7] = onReply;
    $[8] = unreadCount;
    $[9] = waveId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <UnreadDividerProvider initialSerialNo={t2} key={t3}>{t4}</UnreadDividerProvider>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export default WaveDropsAll;